import React, { useState } from "react";
import { format } from "date-fns";
import {
  CalendarPlus,
  Check,
  Download,
  FileSpreadsheet,
  Plus,
} from "lucide-react";
import MobileDetailLayout from "../../components/mobile/mobile-detail-layout";
import MobileInfoCard from "../../components/mobile/mobile-info-card";
import MobileSelectionBar from "../../components/mobile/mobile-selection-bar";
import MobilePageSkeleton from "../../components/mobile/mobile-page-skeleton";
import type { StatItem } from "../../components/mobile/types";
import DataTable, {
  DataTableAction,
  TableRowData,
} from "../../components/data-table";
import SelectInput from "../../components/select-input";
import TabBar from "../../components/tab-bar";
import TabButton from "../../components/tab-button";
import { Badge } from "../../components/ui/badge";
import type { MovementActivity, Site } from "../../lib/api";
import {
  activitiesTableColumns,
  invoicesTableColumns,
  quotesTableColumns,
  siteInventoryTableColumns,
} from "../../lib/table-columns";
import { cn, getBadgeVariant } from "../../lib/utils";
import ExtendDaysHiredSheet from "./ExtendDaysHiredSheet";
import {
  formatExtensionDateFrom,
  getDefaultExtensionDateFrom,
  getMovementDaysHired,
} from "./extend-days";
import {
  MOVEMENT_STATUS_OPTIONS,
  MOVEMENT_SUB_TYPE_OPTIONS,
} from "./movement-filter-options";

type Pagination = NonNullable<
  React.ComponentProps<typeof DataTable>["pagination"]
>;

interface RowsSection {
  rows: TableRowData[];
  actions: DataTableAction[];
}

export interface SiteDetailsMobileProps {
  loading: boolean;
  title: string;
  site: Site | null;
  onBack: () => void;
  onRefresh: () => Promise<unknown>;
  onNewInvoice: () => void;
  stats: StatItem[];
  insights: React.ReactNode;
  equipment: RowsSection & { grandTotalWeight: string; onDownload: () => void };
  movements: RowsSection & {
    totalItems: number;
    isFetching: boolean;
    pagination: Pagination;
    subTypeFilter: string;
    onSubTypeFilterChange: (value: string) => void;
    statusFilter: string;
    onStatusFilterChange: (value: string) => void;
    onDownload: () => void;
  };
  quotes: RowsSection;
  invoices: RowsSection;
  extend: {
    movements: MovementActivity[];
    loading: boolean;
    selectedIds: string[];
    onToggle: (id: string, checked: boolean) => void;
    onToggleAll: (checked: boolean) => void;
    load: () => Promise<boolean>;
    reset: () => void;
    dateTo: string;
    onDateToChange: (value: string) => void;
    notes: string;
    onNotesChange: (value: string) => void;
    minDate?: string;
    isInvalid: boolean;
    isSubmitting: boolean;
    submit: () => Promise<boolean>;
  };
}

type SiteTab = "equipments" | "movements" | "quotes" | "invoices";

const SiteDetailsMobile: React.FC<SiteDetailsMobileProps> = ({
  loading,
  title,
  site,
  onBack,
  onRefresh,
  onNewInvoice,
  stats,
  insights,
  equipment,
  movements,
  quotes,
  invoices,
  extend,
}) => {
  const [tab, setTab] = useState<SiteTab>("equipments");
  const [isSelecting, setIsSelecting] = useState(false);
  const [isExtendSheetOpen, setIsExtendSheetOpen] = useState(false);

  if (loading) return <MobilePageSkeleton />;

  const exitSelection = () => {
    setIsSelecting(false);
    setIsExtendSheetOpen(false);
    extend.reset();
  };

  const startSelection = async () => {
    setTab("movements");
    setIsSelecting(true);
    const loaded = await extend.load();
    if (!loaded) setIsSelecting(false);
  };

  const changeTab = (next: SiteTab) => {
    if (isSelecting) exitSelection();
    setTab(next);
  };

  const submitExtension = async () => {
    const extended = await extend.submit();
    if (extended) {
      setIsExtendSheetOpen(false);
      setIsSelecting(false);
    }
  };

  const customer = site?.customer;
  const location = site
    ? [site.street, site.ward, site.district, site.region]
        .filter(Boolean)
        .join(", ")
    : "";

  const selectedMovements = extend.movements.filter((movement) =>
    extend.selectedIds.includes(movement.id),
  );

  const tabs: { key: SiteTab; label: string; count: number }[] = [
    { key: "equipments", label: "Equipments", count: equipment.rows.length },
    { key: "movements", label: "Movements", count: movements.totalItems },
    { key: "quotes", label: "Quotes", count: quotes.rows.length },
    { key: "invoices", label: "Invoices", count: invoices.rows.length },
  ];

  const renderSelectableMovements = () => {
    if (extend.loading) {
      return (
        <div className="space-y-2.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[76px] animate-pulse rounded-2xl bg-gray-200"
            />
          ))}
        </div>
      );
    }
    if (extend.movements.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
          No eligible movements found for this site.
        </div>
      );
    }
    return (
      <div className="space-y-2.5">
        {extend.movements.map((movement) => {
          const checked = extend.selectedIds.includes(movement.id);
          const date = movement.plannedDate || movement.createdAt;
          const dateFrom = getDefaultExtensionDateFrom(movement);
          const daysHired = getMovementDaysHired(movement);
          return (
            <button
              key={movement.id}
              type="button"
              role="checkbox"
              aria-checked={checked}
              disabled={extend.isSubmitting}
              onClick={() => extend.onToggle(movement.id, !checked)}
              className={cn(
                "flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-left transition-colors active:bg-gray-50 [-webkit-tap-highlight-color:transparent]",
                checked
                  ? "border-primary-500 ring-1 ring-primary-500"
                  : "border-gray-100",
              )}
            >
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2",
                  checked
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-gray-300",
                )}
              >
                {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-gray-900">
                  {movement.reference?.trim() || movement.id.slice(0, 8)}
                </span>
                <span className="block truncate text-xs text-gray-500">
                  {movement.activitySubType}
                  {date ? ` · ${format(new Date(date), "MMM dd, yyyy")}` : ""}
                  {Number.isFinite(daysHired) ? ` · ${daysHired} days` : ""}
                </span>
                {dateFrom && (
                  <span className="block truncate text-xs text-gray-400">
                    Extends from {formatExtensionDateFrom(dateFrom)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderMovements = () => {
    if (isSelecting) {
      return (
        <div className="space-y-3">
          <div className="px-1">
            <p className="text-sm font-semibold text-gray-900">
              Select movements to extend
            </p>
            <p className="text-xs text-gray-500">
              DELIVER and TRANSFER outflow only
            </p>
          </div>
          {renderSelectableMovements()}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <SelectInput
            value={movements.subTypeFilter}
            options={MOVEMENT_SUB_TYPE_OPTIONS}
            placeholder="Subtype"
            onChange={movements.onSubTypeFilterChange}
          />
          <SelectInput
            value={movements.statusFilter}
            options={MOVEMENT_STATUS_OPTIONS}
            placeholder="Status"
            onChange={movements.onStatusFilterChange}
          />
        </div>
        {movements.totalItems > 0 && (
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-medium text-gray-500">
              {movements.totalItems.toLocaleString()} movement
              {movements.totalItems === 1 ? "" : "s"}
            </p>
            <button
              type="button"
              onClick={() => void startSelection()}
              className="border-0 bg-transparent p-0 text-[13px] font-semibold text-primary-600 active:opacity-60"
            >
              Extend days hired
            </button>
          </div>
        )}
        <DataTable
          columns={activitiesTableColumns}
          data={movements.rows}
          actions={movements.actions}
          loading={movements.isFetching}
          pagination={movements.pagination}
        />
      </div>
    );
  };

  const renderTab = () => {
    switch (tab) {
      case "equipments":
        return (
          <div className="space-y-3">
            <DataTable
              columns={siteInventoryTableColumns}
              data={equipment.rows}
              actions={equipment.actions}
            />
            {equipment.rows.length > 0 && (
              <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-4 py-3">
                <span className="text-[13px] text-gray-500">
                  Grand total weight
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {equipment.grandTotalWeight}
                </span>
              </div>
            )}
          </div>
        );
      case "movements":
        return renderMovements();
      case "quotes":
        return (
          <DataTable
            columns={quotesTableColumns}
            data={quotes.rows}
            actions={quotes.actions}
          />
        );
      case "invoices":
        return (
          <DataTable
            columns={invoicesTableColumns}
            data={invoices.rows}
            actions={invoices.actions}
          />
        );
    }
  };

  return (
    <>
      <MobileDetailLayout
        eyebrow="Site"
        title={title}
        subtitle={customer?.fullName}
        badge={
          site?.status ? (
            <Badge variant={getBadgeVariant(site.status)}>{site.status}</Badge>
          ) : undefined
        }
        onBack={onBack}
        onRefresh={onRefresh}
        primaryAction={{
          label: "New Invoice",
          icon: Plus,
          onClick: onNewInvoice,
        }}
        actions={[
          {
            label: "Extend days hired",
            icon: CalendarPlus,
            onClick: () => void startSelection(),
            disabled: movements.totalItems === 0,
          },
          {
            label: "Download equipments Excel",
            icon: Download,
            onClick: equipment.onDownload,
            disabled: equipment.rows.length === 0,
          },
          {
            label: "Download movements Excel",
            icon: FileSpreadsheet,
            onClick: movements.onDownload,
            disabled: movements.totalItems === 0,
          },
        ]}
        stats={stats}
        insights={insights}
        bottomBar={
          isSelecting ? (
            <MobileSelectionBar
              selectedCount={extend.selectedIds.length}
              totalCount={extend.movements.length}
              onToggleAll={() =>
                extend.onToggleAll(
                  extend.selectedIds.length !== extend.movements.length,
                )
              }
              onCancel={exitSelection}
              actionLabel="Extend"
              onAction={() => setIsExtendSheetOpen(true)}
              actionDisabled={
                extend.loading ||
                extend.isSubmitting ||
                extend.selectedIds.length === 0
              }
            />
          ) : undefined
        }
      >
        <MobileInfoCard
          title="Details"
          rows={[
            customer?.fullName && {
              label: "Customer",
              value: customer.fullName,
            },
            customer?.phoneNumber && {
              label: "Phone",
              value: customer.phoneNumber,
              href: `tel:${customer.phoneNumber}`,
            },
            !!location && { label: "Location", value: location },
            site?.country && { label: "Country", value: site.country },
            site?.status && {
              label: "Status",
              value: (
                <Badge variant={getBadgeVariant(site.status)}>
                  {site.status}
                </Badge>
              ),
            },
            site?.createdAt && {
              label: "Created",
              value: format(new Date(site.createdAt), "MMM dd, yyyy"),
            },
          ]}
        />

        <TabBar>
          {tabs.map((item) => (
            <TabButton
              key={item.key}
              label={item.label}
              count={item.count}
              active={tab === item.key}
              onClick={() => changeTab(item.key)}
            />
          ))}
        </TabBar>

        {renderTab()}
      </MobileDetailLayout>

      <ExtendDaysHiredSheet
        open={isExtendSheetOpen}
        onClose={() => setIsExtendSheetOpen(false)}
        selectedMovements={selectedMovements}
        dateTo={extend.dateTo}
        onDateToChange={extend.onDateToChange}
        notes={extend.notes}
        onNotesChange={extend.onNotesChange}
        minDate={extend.minDate}
        isInvalid={extend.isInvalid}
        isSubmitting={extend.isSubmitting}
        onSubmit={() => void submitExtension()}
      />
    </>
  );
};

export default SiteDetailsMobile;
